import { useEffect, useState, useRef } from "react";
import tw from "tailwind-styled-components";
import { jsPDF } from "jspdf";
import moment from "moment";
import TransactionTable from "../PaymentsTables";
import DuesTable from "../PendingPaymentTable";
import NotFoundModel from "../../NotFoundModel";
import { MdOutlineChevronRight } from "react-icons/md";
import { BoxHead, DownloadBtn, Underline } from "../../Styles/PageStyles";
import { getLandlordTransactionFor, getModeName } from "../../../helpers";
import { toast } from "react-toastify";

const formatAmount = (value) =>
    new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const formatDate = (value) => {
    if (!value) return "-";
    const m = moment(value);
    return m.isValid() ? m.format("DD/MM/YY") : "-";
};

const getUtr = (row) => {
    const candidates = [
        row?.bankRefNum,
    ];
    const found = candidates.find(
        (v) => v !== null && v !== undefined && String(v).trim() !== ""
    );
    return found ? String(found) : "-";
};

// const buildNarration = (row) => {
//     const parts = [];

//     const mode = getModeName(row?.paymentMethod);
//     if (mode && mode !== "-") parts.push(mode);

//     if (row?.paidByName) parts.push(row.paidByName);

//     const ref = getUtr(row);
//     if (ref && ref !== "-") parts.push(ref);

//     if (row?.landlordName) parts.push(row.landlordName);

//     const paymentFor = getLandlordTransactionFor(row);
//     if (paymentFor && paymentFor !== "-") parts.push(paymentFor);

//     if (row?.description && String(row.description).trim()) {
//         parts.push(String(row.description).trim());
//     }

//     return parts.length ? parts.join(" - ") : "Payment Entry";
// };


const buildNarration = (row) => {
    const paidBy = row?.paidByName?.trim();
    const paidTo = row?.landlordName?.trim();
    const paymentFor = getLandlordTransactionFor(row);
    const mode = getModeName(row?.paymentMethod);
    const utr = getUtr(row);
    const description = row?.description?.trim();

    const valid = (value) => value && value !== "-";

    let narration = "Payment";

    if (paidBy && paidTo) {
        narration = `Payment made by ${paidBy} to ${paidTo}`;
    } else if (paidBy) {
        narration = `Payment made by ${paidBy}`;
    } else if (paidTo) {
        narration = `Payment made to ${paidTo}`;
    }

    if (valid(paymentFor)) {
        narration += ` for ${paymentFor}`;
    }

    if (valid(mode)) {
        narration += ` via ${mode}`;
    }

    if (valid(utr)) {
        narration += ` (UTR: ${utr})`;
    }

    if (valid(description)) {
        narration += `. Remarks: ${description}`;
    }

    return narration;
};

const Passbook = ({
    transData,
    pendingData,
    summary,
    handleMarkAsPaid,
    setViewDetail,
    setSelectedData,
}) => {
    const [selectedId, setSelectedId] = useState(1);
    const [selectedTable, setSelectedTable] = useState("totalPayments");
    const [securityData, setSecurityData] = useState();
    const [isGenerating, setIsGenerating] = useState(false);

    const pdfContentRef = useRef(null);

    useEffect(() => {
        const security = (transData || []).filter((tx) => tx.type === 43);
        setSecurityData(security);
    }, [transData]);

    const handleBoxClick = (type) => {
        setSelectedTable(type);
    };

    const activeData = transData || [];

    const handleDownloadPDF = async () => {
        if (isGenerating) return;
        if (!activeData || activeData.length === 0) {
            toast.error("No records to export.");
            return;
        }

        setIsGenerating(true);

        try {
            await new Promise((r) => setTimeout(r, 50));
            await document.fonts.ready;

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const margin = 10;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const contentWidth = pageWidth - margin * 2;

            const generatedAt = new Date().toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });

            const columns = [
                { label: "Date", width: 18, get: (r) => formatDate(r.paidDate || r.createdAt) },
                { label: "Narration", width: 66, get: (r) => buildNarration(r) },
                { label: "UTR No.", width: 40, bold: true, get: (r) => getUtr(r) },
                // { label: "Paid By", width: 24, get: (r) => r.paidByName || "-" },
                { label: "Property", width: 26, get: (r) => r.propertyName || "-" },
                { label: "Amount", width: 30, align: "right", get: (r) => formatAmount(r.amount ?? 0) },
            ];

            const totalColWidth = columns.reduce((s, c) => s + c.width, 0);
            const scale = contentWidth / totalColWidth;
            const tableHeaderHeight = 9;
            const rowPaddingY = 4;
            const lineHeight = 4;
            const minRowHeight = 8;
            const tableTopOffset = 24;
            const bottomReserve = 20;

            const wrapText = (text, maxWidth, fontSize = 8) => {
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(fontSize);
                const words = String(text ?? "").split(/\s+/);
                const lines = [];
                let current = "";
                words.forEach((word) => {
                    const test = current ? current + " " + word : word;
                    if (pdf.getTextWidth(test) <= maxWidth) {
                        current = test;
                    } else {
                        if (current) lines.push(current);
                        current = word;
                    }
                });
                if (current) lines.push(current);
                return lines.length ? lines : [""];
            };
            
            const landlordName = activeData?.[0]?.landlordName?.trim() || "Landlord";

            const drawPageHeader = () => {
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(12);
                pdf.setTextColor(15, 23, 42);
                // pdf.text("Landlord Ledger", margin, 14);
                pdf.text(`${landlordName} Ledger`, margin, 14);

                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(8);
                pdf.setTextColor(100, 116, 139);
                pdf.text(generatedAt, pageWidth - margin, 14, { align: "right" });

                pdf.setDrawColor(226, 232, 240);
                pdf.setLineWidth(0.2);
                pdf.line(margin, 18, pageWidth - margin, 18);
            };

            const drawTableHeader = (y) => {
                pdf.setFillColor(241, 245, 249);
                pdf.rect(margin, y, contentWidth, tableHeaderHeight, "F");

                pdf.setDrawColor(203, 213, 225);
                pdf.setLineWidth(0.2);
                pdf.line(margin, y, pageWidth - margin, y);
                pdf.line(margin, y + tableHeaderHeight, pageWidth - margin, y + tableHeaderHeight);

                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(8.5);
                pdf.setTextColor(30, 41, 59);

                let cx = margin + 2;
                columns.forEach((col) => {
                    const w = col.width * scale;
                    if (col.align === "right") {
                        pdf.text(col.label, cx + w - 4, y + 6, { align: "right" });
                    } else {
                        pdf.text(col.label, cx, y + 6);
                    }
                    cx += w;
                });
            };

            const measuredRows = activeData.map((row) => {
                const narration = buildNarration(row);
                const narrationLines = wrapText(narration, columns[1].width * scale - 4, 8);
                const height = Math.max(
                    minRowHeight,
                    narrationLines.length * lineHeight + rowPaddingY
                );
                return { row, narrationLines, height };
            });

            const usableHeight = pageHeight - tableTopOffset - bottomReserve;
            const availableForRows = usableHeight - tableHeaderHeight;
            const pages = [];
            let currentPage = [];
            let usedHeight = 0;

            measuredRows.forEach((measured) => {
                if (usedHeight + measured.height > availableForRows) {
                    if (currentPage.length > 0) {
                        pages.push(currentPage);
                    }
                    currentPage = [measured];
                    usedHeight = measured.height;
                } else {
                    currentPage.push(measured);
                    usedHeight += measured.height;
                }
            });
            if (currentPage.length > 0) pages.push(currentPage);

            const totalPages = pages.length;
            const totalAmount = activeData.reduce(
                (sum, r) => sum + (Number(r.amount ?? r.balance) || 0),
                0
            );

            pages.forEach((pageRows, pageIndex) => {
                if (pageIndex > 0) pdf.addPage();

                drawPageHeader();

                let y = tableTopOffset;
                drawTableHeader(y);
                y += tableHeaderHeight;


                pageRows.forEach((measured, rowIdx) => {
                    const { row, narrationLines, height } = measured;

                    if (rowIdx % 2 === 1) {
                        pdf.setFillColor(248, 250, 252);
                        pdf.rect(margin, y, contentWidth, height, "F");
                    }
                    pdf.setDrawColor(226, 232, 240);
                    pdf.setLineWidth(0.15);
                    pdf.line(margin, y + height, pageWidth - margin, y + height);

                    let cellX = margin + 2;
                    columns.forEach((col, colIdx) => {
                        const w = col.width * scale;
                        const innerWidth = w - 4;

                        pdf.setFont("helvetica", col.bold ? "bold" : "normal");
                        pdf.setFontSize(8);
                        pdf.setTextColor(col.bold ? 15 : 51, col.bold ? 23 : 65, col.bold ? 42 : 85);

                        if (colIdx === 1) {
                            const textBlockHeight = narrationLines.length * lineHeight;
                            const startY =
                                y + (height - textBlockHeight) / 2 + 3;
                            narrationLines.forEach((line, li) => {
                                pdf.text(line, cellX, startY + li * lineHeight);
                            });
                        } else {
                            const rawValue = String(col.get(row) ?? "-");
                            let value = rawValue;
                            if (pdf.getTextWidth(value) > innerWidth) {
                                while (
                                    value.length > 1 &&
                                    pdf.getTextWidth(value + "…") > innerWidth
                                ) {
                                    value = value.slice(0, -1);
                                }
                                value = value + "…";
                            }

                            const textY = y + height / 2 + 1;

                            if (col.align === "right") {
                                pdf.text(value, cellX + w - 4, textY, { align: "right" });
                            } else {
                                pdf.text(value, cellX, textY);
                            }
                        }

                        cellX += w;
                    });

                    y += height;
                });

                if (pageIndex === totalPages - 1) {
                    // y += 2;

                    pdf.setFillColor(241, 245, 249);
                    pdf.rect(margin, y, contentWidth, 10, "F");
                    pdf.setDrawColor(203, 213, 225);
                    pdf.setLineWidth(0.3);
                    pdf.line(margin, y, pageWidth - margin, y);
                    pdf.line(margin, y + 10, pageWidth - margin, y + 10);

                    pdf.setFont("helvetica", "bold");
                    pdf.setFontSize(8);
                    pdf.setTextColor(30, 41, 59);
                    pdf.text("TOTAL", margin + 4, y + 6.5);
                    pdf.text(
                        `Rs. ${formatAmount(totalAmount)}`,
                        pageWidth - margin - 4,
                        y + 6.5,
                        { align: "right" }
                    );
                }

                const footerY = pageHeight - 10;
                pdf.setDrawColor(226, 232, 240)
                pdf.setLineWidth(0.2);
                const lineWidth = 100;
                const centerX = pageWidth / 2;

                pdf.line(
                    centerX - lineWidth,
                    footerY - 6,
                    centerX + lineWidth,
                    footerY - 6
                );
                // pdf.line(margin, footerY - 6, pageWidth - margin, footerY - 6);
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(8);
                //pdf.setTextColor(226, 232, 240);
                pdf.setTextColor(71, 85, 105);
                pdf.text("Generated by Kipinn", centerX, footerY, {
                    align: "center",
                    baseline: "middle",
                });
                // pdf.text(
                //     `Page ${pageIndex + 1} of ${totalPages}`,
                //     pageWidth - margin,
                //     footerY,
                //     { align: "right" }
                // );
            });

            pdf.save(`Landlord-Ledger-${Date.now()}.pdf`);
        } catch (error) {
            console.error("PDF export failed:", error);
        } finally {
            setIsGenerating(false);
        }
    };
    return (
        <>
            <Wrapper>
                <BoxHead>
                    <BoxTitle className="!mb-0 whitespace-nowrap">
                        Passbook
                        <Underline />
                    </BoxTitle>
                    <DownloadBtn
                        type="button"
                        onClick={handleDownloadPDF}
                        disabled={isGenerating}
                    >
                        {isGenerating ? "Generating..." : "Download"}
                    </DownloadBtn>
                </BoxHead>

                <ScrollCard style={{ scrollBehavior: "smooth" }}>
                    <Card
                        selectedId={selectedId}
                        index={1}
                        onClick={() => {
                            setSelectedId(1);
                            handleBoxClick("totalPayments");
                        }}
                    >
                        <div>
                            <Value>{summary?.collection || 0}</Value>
                            <Label>
                                {"Total Payments"}
                                <MdOutlineChevronRight
                                    className="text-gray-300"
                                    size={24}
                                />
                            </Label>
                        </div>
                    </Card>

                    <Card
                        selectedId={selectedId}
                        index={2}
                        onClick={() => {
                            setSelectedId(2);
                            handleBoxClick("totalDues");
                        }}
                    >
                        <div>
                            <Value>{summary?.totalPending || 0}</Value>
                            <Label>
                                {"Total Dues"}
                                <MdOutlineChevronRight
                                    className="text-gray-300"
                                    size={24}
                                />
                            </Label>
                        </div>
                    </Card>

                    <Card
                        selectedId={selectedId}
                        index={3}
                        onClick={() => {
                            setSelectedId(3);
                            handleBoxClick("security");
                        }}
                    >
                        <div>
                            <Value>{summary?.security || 0}</Value>
                            <Label>
                                {"Security Paid"}
                                <MdOutlineChevronRight
                                    className="text-gray-300"
                                    size={24}
                                />
                            </Label>
                        </div>
                    </Card>
                </ScrollCard>
            </Wrapper>

            <div ref={pdfContentRef} className="hidden" aria-hidden="true" />

            <div className="mt-4">
                {selectedTable === "totalPayments" && (
                    <>
                        {transData && (
                            <TableWrapper>
                                <TransactionTable
                                    apiData={transData || []}
                                    setSelectedData={setSelectedData}
                                    setViewDetail={setViewDetail}
                                />
                                {transData?.length === 0 && <NotFoundModel />}
                            </TableWrapper>
                        )}
                    </>
                )}

                {selectedTable === "totalDues" && (
                    <>
                        {pendingData && (
                            <TableWrapper>
                                <DuesTable
                                    apiData={pendingData}
                                    setSelectedData={setSelectedData}
                                    handleMarkAsPaid={(row, paymentType) =>
                                        handleMarkAsPaid(row, paymentType)
                                    }
                                />
                                {pendingData?.length === 0 && (
                                    <NotFoundModel msg="No Dues Pending" />
                                )}
                            </TableWrapper>
                        )}
                    </>
                )}

                {selectedTable === "security" && (
                    <>
                        {securityData && (
                            <TableWrapper>
                                <TransactionTable
                                    apiData={securityData || []}
                                    setSelectedData={setSelectedData}
                                    setViewDetail={setViewDetail}
                                />
                                {securityData?.length === 0 && (
                                    <NotFoundModel />
                                )}
                            </TableWrapper>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

// ---------- styled ----------
const Wrapper = tw.div`space-y-4 mt-2`;
const TableWrapper = tw.h1`my-8 border bg-white rounded-md`;
const ScrollCard = tw.div`w-full overflow-x-auto flex space-x-4 py-1`;
const Card = tw.div`
  min-w-[200px] p-3 rounded-md bg-white shadow-md cursor-pointer
  hover:shadow-sm hover:shadow-primary-color
  ${({ selectedId, index }) =>
        selectedId === index ? "shadow-sm shadow-primary-color" : ""}
`;
const Label = tw.div`
  text-gray-500 text-sm break-words flex items-center w-full mt-2
  justify-between whitespace-normal
`;
const Value = tw.p`text-xl sm:text-2xl font-bold text-black`;
const BoxTitle = tw.h2`
  ${(p) => (p.$fullWidth ? "flex inline-flex" : "justify-center")}
  text-lg text-black font-semibold space-x-2 items-center mb-5 rounded-md
`;

export default Passbook;